import Phaser from 'phaser'
import { GAME_WIDTH, GAME_HEIGHT, INVADER_TYPES } from '../config/game.config'
import { eventManager, eventThemeManager } from '../events'
import type { ThemedInvader } from '../events'

export class PreloadScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics
  private progressBox!: Phaser.GameObjects.Graphics
  private loadingText!: Phaser.GameObjects.Text
  private eventText!: Phaser.GameObjects.Text

  constructor() {
    super({ key: 'PreloadScene' })
  }

  preload(): void {
    this.createLoadingUI()

    // Register loading progress events
    this.load.on('progress', this.onProgress, this)
    this.load.on('complete', this.onComplete, this)

    // Load game assets
    this.load.image('player', 'assets/player.png')
  }

  async create(): Promise<void> {
    // Initialize event system and fetch current event
    this.loadingText.setText('Checking for events...')

    try {
      const event = await eventManager.init()

      if (event) {
        // Show event notification
        this.showEventNotification(event.name, event.icon)
      }
    } catch (err) {
      console.warn('Failed to initialize events:', err)
    }

    // Generate invader sprites (themed or default)
    this.loadingText.setText('Generating sprites...')
    this.generateInvaderSprites()

    // Clean up loading UI
    this.progressBar.destroy()
    this.progressBox.destroy()
    this.loadingText.destroy()
    if (this.eventText) {
      // Give time to show event notification before destroying
      await new Promise((resolve) => setTimeout(resolve, 1000))
      this.eventText.destroy()
    }

    // Start the main game scene
    this.scene.start('GameScene')
  }

  private showEventNotification(eventName: string, icon: string): void {
    const centerX = GAME_WIDTH / 2
    const centerY = GAME_HEIGHT / 2 + 60

    this.eventText = this.add.text(centerX, centerY, `${icon} ${eventName} Active!`, {
      fontFamily: 'monospace',
      fontSize: '16px',
      color: '#ffff00',
    })
    this.eventText.setOrigin(0.5)
  }

  private generateInvaderSprites(): void {
    // Check if we have themed invaders from an active event
    const invaderTypes = eventThemeManager.getInvaderTypes()

    // Generate sprites for each invader type with 2 animation frames
    for (const invaderType of invaderTypes) {
      this.generateInvaderTexture(
        `${invaderType.key}_1`,
        invaderType.pattern1,
        invaderType.color,
        invaderType.width,
        invaderType.height
      )
      this.generateInvaderTexture(
        `${invaderType.key}_2`,
        invaderType.pattern2,
        invaderType.color,
        invaderType.width,
        invaderType.height
      )
    }

    // Also generate default invaders if we're using themed ones
    // This ensures we have fallback textures if needed
    if (eventThemeManager.hasActiveTheme()) {
      for (const invaderType of INVADER_TYPES) {
        // Only generate if not already generated (different keys)
        const themedKeys = invaderTypes.map((t: ThemedInvader) => t.key)
        if (!themedKeys.includes(invaderType.key)) {
          this.generateInvaderTexture(
            `${invaderType.key}_1`,
            invaderType.pattern1,
            invaderType.color,
            invaderType.width,
            invaderType.height
          )
          this.generateInvaderTexture(
            `${invaderType.key}_2`,
            invaderType.pattern2,
            invaderType.color,
            invaderType.width,
            invaderType.height
          )
        }
      }
    }
  }

  private generateInvaderTexture(
    key: string,
    pattern: number[][],
    color: number,
    width: number,
    _height: number
  ): void {
    const pixelSize = Math.floor(width / pattern[0].length)
    const textureWidth = pattern[0].length * pixelSize
    const textureHeight = pattern.length * pixelSize

    const graphics = this.make.graphics({ x: 0, y: 0 })

    // Draw the invader pattern
    for (let row = 0; row < pattern.length; row++) {
      for (let col = 0; col < pattern[row].length; col++) {
        if (pattern[row][col] === 1) {
          graphics.fillStyle(color, 1)
          graphics.fillRect(
            col * pixelSize,
            row * pixelSize,
            pixelSize,
            pixelSize
          )
        }
      }
    }

    // Generate texture from graphics
    graphics.generateTexture(key, textureWidth, textureHeight)
    graphics.destroy()
  }

  private createLoadingUI(): void {
    const centerX = GAME_WIDTH / 2
    const centerY = GAME_HEIGHT / 2

    // Create progress box background
    this.progressBox = this.add.graphics()
    this.progressBox.fillStyle(0x222222, 0.8)
    this.progressBox.fillRect(centerX - 160, centerY - 25, 320, 50)

    // Create progress bar
    this.progressBar = this.add.graphics()

    // Create loading text
    this.loadingText = this.add.text(centerX, centerY - 50, 'Loading...', {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#ffffff',
    })
    this.loadingText.setOrigin(0.5)
  }

  private onProgress(value: number): void {
    const centerX = GAME_WIDTH / 2
    const centerY = GAME_HEIGHT / 2

    this.progressBar.clear()
    this.progressBar.fillStyle(0x00ff00, 1)
    this.progressBar.fillRect(centerX - 150, centerY - 15, 300 * value, 30)
  }

  private onComplete(): void {
    this.loadingText.setText('Complete!')
  }
}
